import type { Rng } from './dsp/rng';

export type { Rng };

export const clamp = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v));
export const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

// `rng` defaults to Math.random at call time, so tests that replace Math.random still apply.
export const randInt = (a: number, b: number, rng: Rng = Math.random): number => a + Math.floor(rng() * (b - a + 1));
export const choice = <T>(arr: readonly T[], rng: Rng = Math.random): T => arr[Math.floor(rng() * arr.length)] as T;

export function shuffle<T>(arr: readonly T[], rng: Rng = Math.random): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j] as T, a[i] as T];
  }
  return a;
}

export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  /** Runs a pending call now. */
  flush(): void;
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): Debounced<A> {
  let t: ReturnType<typeof setTimeout> | undefined;
  let pending: A | null = null;
  const run = () => {
    clearTimeout(t);
    t = undefined;
    const args = pending;
    pending = null;
    if (args) fn(...args);
  };
  const d = (...args: A) => {
    pending = args;
    clearTimeout(t);
    t = setTimeout(run, ms);
  };
  d.flush = run;
  return d;
}

/** Local calendar day as YYYY-MM-DD. */
export function dateKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

/** "2026-10-24" → "24 Ekim 2026". */
export function fmtDate(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const month = MONTHS[(m ?? 0) - 1];
  return y && month && d ? `${d} ${month} ${y}` : key;
}

/** A decimal number the Turkish way, with a comma (copy guide: "0,8 saniye"): fmtDec(7.62, 1) → "7,6",
    fmtDec(0.8) → "0,8", fmtDec(2) → "2". */
export function fmtDec(n: number, digits?: number): string {
  return (digits == null ? String(n) : n.toFixed(digits)).replace('.', ',');
}

export function fmtTime(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** FNV-1a string hash, used to seed deterministic generators. */
export function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Scroll the page to the top (no-op outside the browser). */
export function scrollTop(): void {
  try {
    window.scrollTo(0, 0);
  } catch {
    /* not in a browser */
  }
}

/** Decibel-style 0..100 meter position for an RMS level. */
export const levelPct = (v: number): number => clamp((20 * Math.log10(Math.max(v, 1e-5)) + 60) / 60, 0, 1) * 100;
